// Work in the Teaching Content Generator tools (activities, worksheet, lesson plan,
// presentation, suggested videos) is kept here, so a refresh or a switch to another
// tool does not throw it away.
//
// - sessionStorage, not localStorage: it survives a refresh and a tool switch, and is
//   gone when the browser tab is closed. That is safer on shared school computers.
// - One draft per tool and per user. A page only ever reads its own user's key, and
//   every draft is removed at logout (clearUserBrowserData).
// - Storage can be full or blocked (private mode). Every read and write is wrapped, and
//   the page then simply keeps what is on screen.

const PREFIX = 'cg_draft_';

export const toolDraftKey = (tool, userId) => `${PREFIX}${tool}_${userId || 'guest'}`;

export function loadToolDraft(key) {
  try {
    const raw = window.sessionStorage.getItem(key);
    const draft = raw ? JSON.parse(raw) : null;
    return draft && typeof draft === 'object' && !Array.isArray(draft) ? draft : null;
  } catch (e) {
    return null;
  }
}

// `draft` is an object, or the JSON text of one. Returns false when it could not be saved
// (storage full or blocked). An older copy is then removed, so a refresh never shows
// out-of-date work.
export function saveToolDraft(key, draft) {
  try {
    window.sessionStorage.setItem(key, typeof draft === 'string' ? draft : JSON.stringify(draft));
    return true;
  } catch (e) {
    clearToolDraft(key);
    return false;
  }
}

// Change part of a saved draft. `change` is an object, or a function that gets the saved
// draft and returns the fields to change. Does nothing when no draft is saved: for
// example the teacher signed out while the request was still running.
export function patchToolDraft(key, change) {
  const draft = loadToolDraft(key);
  if (!draft) return false;
  const fields = typeof change === 'function' ? change(draft) : change;
  return saveToolDraft(key, { ...draft, ...fields });
}

export function clearToolDraft(key) {
  try { window.sessionStorage.removeItem(key); } catch (e) { /* storage blocked: nothing saved */ }
}

// ---- Requests still running in this tab ----
// A request keeps running when the teacher leaves the page. Its handler saves the result
// into the draft when it arrives. A page that is opened again meanwhile waits for it here.
const running = new Map(); // draft key -> { count, promise, resolve }

// Call when a request starts. Returns the function to call when it ends (success or not).
export function startToolRequest(key) {
  let entry = running.get(key);
  if (!entry) {
    entry = { count: 0 };
    entry.promise = new Promise((resolve) => { entry.resolve = resolve; });
    running.set(key, entry);
  }
  entry.count += 1;
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    if (running.get(key) !== entry) return; // cleared at logout
    entry.count -= 1;
    if (entry.count <= 0) {
      running.delete(key);
      entry.resolve();
    }
  };
}

// A promise that resolves when every running request for this key has ended, or null.
export const runningToolRequest = (key) => running.get(key)?.promise || null;

// Logout: forget every draft and every running request, for all users.
export function clearAllToolDrafts() {
  running.forEach((entry) => entry.resolve());
  running.clear();
  clearSavedToolDrafts();
}

// Remove every saved draft, for all users, but leave running requests alone (a result that
// lands later finds no draft and is dropped). Used after a page crash: the saved work may
// be what broke the page, and it would break it again on every reload.
export function clearSavedToolDrafts() {
  try {
    const keys = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key && key.startsWith(PREFIX)) keys.push(key);
    }
    keys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch (e) {
    /* storage blocked: nothing saved */
  }
}
